import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setIsPlaced, setNewPolygon } from "../../redux/polygonPlacement";
import {
  objectSelfPartKeyAndProp,
  POINT_TYPE,
  equalityThreshold,
} from "./utils";
import PointPlacer from "./PointPlacer";
import { PointAndLines } from "./PointsAndLines";
import { isArrayEqual } from "../utils";
import PlacedPolygons from "./PlacedPolygons";
import { useOrbit } from "../CustomHooks";

const PolygonPlacer = ({ orbitRef }) => {
  const dispatch = useDispatch();
  const { newPolygon, isPlacementMode, isPlaced } = useSelector(
    (state) => state.polygonPlacement
  );

  const [newPoint, setNewPoint] = useState();
  const newPointArray = useMemo(
    () => (newPoint ? [newPoint.x, newPoint.y, newPoint.z] : null),
    [newPoint]
  );
  const startPoint = useMemo(() => newPolygon?.verts?.[0], [newPolygon]);
  const handlePointPlace = useCallback(() => {}, []);

  const canClosePolygon = newPolygon?.verts?.length >= 3;
  const isSnapPointActive = canClosePolygon;

  const handleEditingPositionChange = useCallback(
    (newPosition) => {
      setNewPoint(newPosition);

      const newPositionArray = [newPosition.x, newPosition.y, newPosition.z];

      if (!canClosePolygon) return;

      const isOnStartPoint = isArrayEqual(
        newPositionArray,
        startPoint,
        equalityThreshold
      );
      if (isOnStartPoint) {
        dispatch(setIsPlaced(true));
        dispatch(
          setNewPolygon({
            ...newPolygon,
            verts: [...(newPolygon?.verts ?? []), newPositionArray],
          })
        );
      }
    },
    [startPoint, dispatch, newPolygon, canClosePolygon]
  );

  const setEditingPosition = useCallback(
    (newPosition) => {
      dispatch(
        setNewPolygon({
          ...newPolygon,
          verts: [...(newPolygon?.verts ?? []), newPosition],
        })
      );
    },
    [dispatch, newPolygon]
  );

  const currentPolygonVerts = [...(newPolygon?.verts ?? [])];

  const currentPolygonAndNewPoint = newPoint
    ? [...currentPolygonVerts, newPointArray]
    : currentPolygonVerts;

  const pointsAndLinesComponentProps = {
    pointsProps: {
      radius: 0.25,
      groupProps: {
        "rotation-x": 0,
      },
      objectProps: {
        ...objectSelfPartKeyAndProp.prop,
      },
    },
    linesProps: {
      ...objectSelfPartKeyAndProp.prop,
    },
  };

  const { disableOrbit, enableOrbit } = useOrbit();
  useEffect(() => {
    if (isPlacementMode && currentPolygonAndNewPoint.length > 1) {
      disableOrbit();
    } else {
      enableOrbit();
    }
  }, [isPlacementMode, currentPolygonAndNewPoint, disableOrbit, enableOrbit]);

  return (
    <>
      <PlacedPolygons />

      {isPlacementMode && (
        <>
          {!isPlaced && (
            <PointPlacer
              onPlacePoint={handlePointPlace}
              setEditingPosition={setEditingPosition}
              orbitRef={orbitRef}
              type={POINT_TYPE.INVISIBLE_POINT}
              isAnimationActive={false}
              onSpherePositionChange={handleEditingPositionChange}
              snapPosition={isSnapPointActive ? startPoint : null}
            />
          )}
          <PointAndLines
            verts={currentPolygonAndNewPoint}
            {...pointsAndLinesComponentProps}
          />
        </>
      )}
    </>
  );
};

export default PolygonPlacer;
